/**
 * 工具函数集
 */
import { GameConfig } from './GameConfig';

export class Utils {
    /** 5c 竞角记忆键（`player.auditionReveal` 的键；ScriptSystem 写、GameState 作废时都走这里，⛔ 两处各拼一遍） */
    static auditionRevealKey(scriptId: string, roleIndex: number): string {
        return `${scriptId}#${roleIndex}`;
    }

    /** 5c 竞角记忆修剪：只留 `auditionQualified` 里还在的那些键
     *  （ScriptSystem 换新清资格、GameState 收口清资格两处同一把尺，不许各写一遍）。
     *  例外一处（2026-11-03 竞角重排批）：月末结转包里的队列行跨这次存盘，
     *  它的成败记忆不许跟着资格一起删——签约门（`ScriptSystem.contractScript`）只认这份记忆。 */
    static pruneAuditionReveal(player: {
        auditionQualified: { scriptId: string; roleIndex: number }[];
        auditionReveal?: Record<string, boolean>;
        monthHandover?: { rows: { scriptId: string; roleIndex: number }[] };
    }): void {
        const map = player.auditionReveal;
        if (!map) return;
        const alive = new Set(player.auditionQualified.map(q => Utils.auditionRevealKey(q.scriptId, q.roleIndex)));
        for (const r of player.monthHandover?.rows ?? []) alive.add(Utils.auditionRevealKey(r.scriptId, r.roleIndex));
        for (const key of Object.keys(map)) if (!alive.has(key)) delete map[key];
    }

    /** 范围随机整数 [min, max] */
    static randomInt(min: number, max: number): number {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    /** 范围随机浮点 [min, max)（粉丝月度扰动/likes 抖动等连续随机用） */
    static randomFloat(min: number, max: number): number {
        return min + Math.random() * (max - min);
    }

    /** 掷 D6 骰子（1-6），每点 = GameConfig.DICE_FACE_WEIGHT 属性（随 ATTR_SCALE 缩放） */
    static rollD6(): number {
        return Utils.randomInt(1, 6);
    }

    /** 加权随机：从 items 中按 weights 权重选一个 */
    static weightedRandom<T>(items: T[], weights: number[]): T {
        const total = weights.reduce((s, w) => s + w, 0);
        let r = Math.random() * total;
        for (let i = 0; i < items.length; i++) {
            r -= weights[i];
            if (r <= 0) return items[i];
        }
        return items[items.length - 1];
    }

    /** 限制值在 [min, max] 之间 */
    static clamp(value: number, min: number, max: number): number {
        return Math.max(min, Math.min(max, value));
    }

    /** 判定：属性×0.5（向下取整） + D6×骰权 ≥ DC（批3-B 折半 + 批3-A 骰权随 ATTR_SCALE 缩放） */
    static checkSkill(attrValue: number, dc: number): { success: boolean; roll: number; total: number } {
        const roll = Utils.rollD6();
        const total = Math.floor(attrValue / 2) + roll * GameConfig.DICE_FACE_WEIGHT;
        return { success: total >= dc, roll, total };
    }

    /** 显示通过概率（用于 UI 上显示选项成功率，与 checkSkill 同式） */
    static successProbability(attrValue: number, dc: number): number {
        // 需要的最低点数（属性折半后与 DC 差值按骰权折算）
        const needed = Math.max(1, Math.ceil((dc - Math.floor(attrValue / 2)) / GameConfig.DICE_FACE_WEIGHT));
        // 6面骰子，>= needed 的概率
        if (needed > 6) return 0;
        if (needed <= 1) return 100;
        return Math.round(((6 - needed + 1) / 6) * 100);
    }

    /** 生成唯一 ID（运行时用，存档用固定 ID） */
    static generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    }

    /** 深拷贝对象 */
    static deepClone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    /** 格式化金钱显示：≥1亿 X.XX亿（最多两位小数，去尾零）；≥1万 X.X万；其余原样 */
    static formatMoney(amount: number): string {
        if (amount >= 1_0000_0000) {
            return parseFloat((amount / 1_0000_0000).toFixed(2)) + '亿';
        }
        if (amount >= 10000) {
            return (amount / 10000).toFixed(1) + '万';
        }
        return amount.toString();
    }

    /**
     * 格式化粉丝/点赞等计数（新浪微博口径，剧宣批用户定稿）：
     * <1万 完整数字（千分位）；≥1万 X.X万 一位小数；≥1亿 X.XX亿 两位小数
     */
    static formatCountCN(amount: number): string {
        if (amount >= 1_0000_0000) return (amount / 1_0000_0000).toFixed(2) + '亿';
        if (amount >= 1_0000) return (amount / 1_0000).toFixed(1) + '万';
        return amount.toLocaleString();
    }

    /**
     * 系统是否开启「减弱动态效果」（WCAG 2.3.3）。
     * CSS 的 @media (prefers-reduced-motion) 只能让动画瞬间完成，
     * 管不到 setInterval 驱动的逐字打字机与骰子滚动——那些必须 JS 侧另判。
     */
    static prefersReducedMotion(): boolean {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    }
}